import React, { useEffect, useState } from "react";
import axios from "axios";
import { FaArrowLeft } from "react-icons/fa";
import Chart from "react-apexcharts";
import TableLoader from "Components/Common/Loader/TableLoader";

const ACTIVITY_LABELS = {
  gang_create: "Gang Create",
  gang_duration: "Gang Duration",
  individual_game: "Games",
  gift_collected: "Gifts Received",
  gift_sent: "Gifts Sent",
  audio_call: "Audio Calls",
  video_call: "Video Calls",
  daily_login: "Daily Login",
};

const EventDashboard = ({ event, onBack }) => {
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchDashboard = () => {
    setLoading(true);
    axios
      .get(
        `${process.env.REACT_APP_API_URL}/event/${event._id}/dashboard`
      )
      .then((response) => {
        setDashboardData(response.data.data);
      })
      .catch((error) => {
        console.error("Error fetching dashboard:", error);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    if (event?._id) {
      fetchDashboard();
    }
  }, [event]);

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  // Prepare activity breakdown chart data
  const getActivityChartData = () => {
    if (!dashboardData?.activityBreakdown) return null;

    const breakdown = dashboardData.activityBreakdown;
    const labels = [];
    const series = [];

    Object.keys(ACTIVITY_LABELS).forEach((key) => {
      const value = breakdown[key] || 0;
      if (value > 0) {
        labels.push(ACTIVITY_LABELS[key]);
        series.push(value);
      }
    });

    return { labels, series };
  };

  // Prepare daily trend chart data
  const getDailyTrendData = () => {
    if (!dashboardData?.dailyTrend || dashboardData.dailyTrend.length === 0)
      return null;

    const categories = dashboardData.dailyTrend.map((d) =>
      new Date(d.date).toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
      })
    );

    return {
      categories,
      pointsSeries: dashboardData.dailyTrend.map((d) => d.totalPoints || 0),
      usersSeries: dashboardData.dailyTrend.map((d) => d.activeUsers || 0),
    };
  };

  const activityChart = getActivityChartData();
  const trendData = getDailyTrendData();

  return (
    <div className="mx-auto mt-5 w-auto rounded-t-2xl">
      <div className="rounded-2xl bg-white py-5 shadow-sm">
        {/* Header */}
        <div className="flex items-center gap-3 p-5 shadow-sm">
          <button
            onClick={onBack}
            className="flex items-center justify-center rounded-full bg-gray-100 p-2 hover:bg-gray-200"
          >
            <FaArrowLeft className="text-gray-600" />
          </button>
          <div>
            <h2 className="text-2xl font-semibold tracking-wide">
              📊 Event Dashboard
            </h2>
            <p className="mt-1 text-sm text-gray-500">
              {event.name} • {formatDate(event.startDate)} —{" "}
              {formatDate(event.endDate)}
            </p>
          </div>
        </div>

        {loading ? (
          <TableLoader />
        ) : !dashboardData ? (
          <div className="py-20 text-center text-gray-400">
            No data available
          </div>
        ) : (
          <>
            {/* Summary Cards */}
            <div className="grid grid-cols-2 gap-4 p-5 md:grid-cols-4">
              <div className="rounded-xl bg-gradient-to-br from-purple-500 to-pink-500 p-4 text-white shadow-md">
                <p className="text-3xl font-bold">
                  {dashboardData.totalParticipants || 0}
                </p>
                <p className="mt-1 text-sm opacity-90">Total Participants</p>
              </div>
              <div className="rounded-xl bg-gradient-to-br from-amber-500 to-orange-500 p-4 text-white shadow-md">
                <p className="text-3xl font-bold">
                  {dashboardData.activityBreakdown?.totalPoints || 0}
                </p>
                <p className="mt-1 text-sm opacity-90">Total Points Earned</p>
              </div>
              <div className="rounded-xl bg-gradient-to-br from-green-500 to-emerald-500 p-4 text-white shadow-md">
                <p className="text-3xl font-bold capitalize">
                  {event.status}
                </p>
                <p className="mt-1 text-sm opacity-90">Event Status</p>
              </div>
              <div className="rounded-xl bg-gradient-to-br from-blue-500 to-cyan-500 p-4 text-white shadow-md">
                <p className="text-3xl font-bold">
                  {event.peakHours?.filter((ph) => ph.isActive)?.length || 0}
                </p>
                <p className="mt-1 text-sm opacity-90">Active Peak Hours</p>
              </div>
            </div>

            {/* Charts */}
            <div className="grid gap-4 p-5 md:grid-cols-2">
              {/* Activity Breakdown Donut */}
              {activityChart && activityChart.series.length > 0 && (
                <div className="rounded-xl border bg-white p-4 shadow-sm">
                  <h3 className="mb-3 text-lg font-semibold text-gray-700">
                    🎯 Points by Activity
                  </h3>
                  <Chart
                    options={{
                      chart: { type: "donut" },
                      labels: activityChart.labels,
                      colors: [
                        "#c51369",
                        "#7694FF",
                        "#FF7676",
                        "#50C8EF",
                        "#1cbe47",
                        "#D5549F",
                        "#F54EA2",
                        "#FFD700",
                      ],
                      legend: { position: "bottom", fontSize: "12px" },
                      dataLabels: { enabled: true },
                      plotOptions: {
                        pie: {
                          donut: {
                            size: "60%",
                            labels: {
                              show: true,
                              total: {
                                show: true,
                                label: "Total",
                                fontSize: "14px",
                                fontWeight: 600,
                              },
                            },
                          },
                        },
                      },
                    }}
                    series={activityChart.series}
                    type="donut"
                    height={320}
                  />
                </div>
              )}

              {/* Daily Trend Line Chart */}
              {trendData && (
                <div className="rounded-xl border bg-white p-4 shadow-sm">
                  <h3 className="mb-3 text-lg font-semibold text-gray-700">
                    📈 Daily Participation Trend
                  </h3>
                  <Chart
                    options={{
                      chart: {
                        type: "area",
                        toolbar: { show: false },
                      },
                      xaxis: {
                        categories: trendData.categories,
                        labels: { style: { fontSize: "11px" } },
                      },
                      yaxis: [
                        {
                          title: { text: "Points" },
                          labels: { style: { fontSize: "11px" } },
                        },
                        {
                          opposite: true,
                          title: { text: "Active Users" },
                          labels: { style: { fontSize: "11px" } },
                        },
                      ],
                      colors: ["#c51369", "#7694FF"],
                      stroke: { curve: "smooth", width: 2 },
                      fill: {
                        type: "gradient",
                        gradient: { opacityFrom: 0.4, opacityTo: 0.1 },
                      },
                      legend: { position: "top" },
                      dataLabels: { enabled: false },
                      tooltip: { shared: true },
                    }}
                    series={[
                      { name: "Points", data: trendData.pointsSeries },
                      {
                        name: "Active Users",
                        data: trendData.usersSeries,
                      },
                    ]}
                    type="area"
                    height={320}
                  />
                </div>
              )}
            </div>

            {/* Activity Breakdown Table */}
            <div className="p-5">
              <h3 className="mb-3 text-lg font-semibold text-gray-700">
                📋 Activity Breakdown
              </h3>
              <div className="overflow-x-auto">
                <table className="w-full border text-left text-sm">
                  <thead className="bg-[#e0ced7] text-gray-700">
                    <tr>
                      <th className="border py-2 text-center">Activity</th>
                      <th className="border py-2 text-center">
                        Total Points
                      </th>
                      <th className="border py-2 text-center">
                        % of Total
                      </th>
                      <th className="border py-2 text-center">
                        Points Per Unit
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.keys(ACTIVITY_LABELS).map((key) => {
                      const points =
                        dashboardData.activityBreakdown?.[key] || 0;
                      const total =
                        dashboardData.activityBreakdown?.totalPoints || 1;
                      const percentage = ((points / total) * 100).toFixed(1);
                      const criteria = event.pointCriteria?.find(
                        (c) => c.activityType === key
                      );

                      return (
                        <tr key={key} className="border-b hover:bg-gray-50">
                          <td className="border py-2 text-center font-medium">
                            {ACTIVITY_LABELS[key]}
                          </td>
                          <td className="border py-2 text-center font-bold text-[#c51369]">
                            {points}
                          </td>
                          <td className="border py-2 text-center">
                            <div className="mx-auto w-24">
                              <div className="h-2 rounded-full bg-gray-200">
                                <div
                                  className="h-2 rounded-full bg-[#c51369]"
                                  style={{
                                    width: `${Math.min(percentage, 100)}%`,
                                  }}
                                />
                              </div>
                              <span className="text-xs text-gray-500">
                                {percentage}%
                              </span>
                            </div>
                          </td>
                          <td className="border py-2 text-center text-xs text-gray-500">
                            {criteria
                              ? `${criteria.pointsPerUnit} / ${criteria.unit}`
                              : "-"}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Peak Hours Info */}
            {event.peakHours?.length > 0 && (
              <div className="p-5">
                <h3 className="mb-3 text-lg font-semibold text-gray-700">
                  ⚡ Peak Hours Configuration
                </h3>
                <div className="grid gap-3 md:grid-cols-3">
                  {event.peakHours.map((ph, i) => (
                    <div
                      key={i}
                      className={`rounded-xl border-2 p-4 ${
                        ph.isActive
                          ? "border-amber-300 bg-amber-50"
                          : "border-gray-200 bg-gray-50 opacity-60"
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-semibold">
                          {ph.label || `Peak Hour #${i + 1}`}
                        </span>
                        <span
                          className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                            ph.isActive
                              ? "bg-green-100 text-green-700"
                              : "bg-gray-200 text-gray-500"
                          }`}
                        >
                          {ph.isActive ? "Active" : "Inactive"}
                        </span>
                      </div>
                      <p className="mt-2 text-lg font-bold text-amber-600">
                        {ph.startTime} — {ph.endTime}
                      </p>
                      <p className="mt-1 text-sm text-gray-600">
                        Multiplier:{" "}
                        <span className="font-bold text-amber-700">
                          {ph.multiplier}x
                        </span>
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default EventDashboard;
